import {createStyles, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import {AgentFoundrySummary} from 'llm/chat-library/foundries/useAgentFoundries';

const useListStyles = createStyles(() => ({
  list: {
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
  },
}));

const useCardStyles = createStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.white,
    width: '100%',
    textAlign: 'left',
    ':hover': {
      borderColor: theme.colors.blue[3],
      backgroundColor: theme.colors.gray[0],
    },
  },
}));

function AgentFoundryCard({
  agentFoundry,
  onSelect,
}: {
  agentFoundry: AgentFoundrySummary;
  onSelect: (agentFoundryId: string) => void;
}): JSX.Element {
  const {classes} = useCardStyles();

  return (
    <UnstyledButton
      component="div"
      role="button"
      tabIndex={0}
      className={classes.card}
      aria-label={`Open ${agentFoundry.name}`}
      onClick={() => {
        onSelect(agentFoundry.id);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(agentFoundry.id);
        }
      }}
    >
      <Text fw="bold" size="sm" truncate={true}>
        {agentFoundry.name}
      </Text>
      {agentFoundry.creatorName && (
        <Text size="xs" c="dimmed">
          by {agentFoundry.creatorName}
        </Text>
      )}
      <Text size="xs" c="dimmed" lineClamp={2}>
        {agentFoundry.description}
      </Text>
    </UnstyledButton>
  );
}

export default function AgentFoundriesList({
  agentFoundries,
  onSelect,
}: {
  agentFoundries: Array<AgentFoundrySummary>;
  onSelect: (agentFoundryId: string) => void;
}): JSX.Element {
  const {classes} = useListStyles();

  if (agentFoundries.length === 0) {
    return (
      <Text fw="normal" c="dimmed" size="sm">
        No foundries yet.
      </Text>
    );
  }

  return (
    <Stack spacing="md" className={classes.list}>
      {agentFoundries.map((agentFoundry) => (
        <AgentFoundryCard key={agentFoundry.id} agentFoundry={agentFoundry} onSelect={onSelect} />
      ))}
    </Stack>
  );
}
